import { useEffect, useReducer } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";
import * as yup from "yup";
import AppButton from "@/components/CustomButton";
import AppTextArea from "@/components/CustomTextArea";
import { SupervisorService, TicketService } from "@/services";
import {
  dropDownStyles,
  Reducer,
  ReducerAction,
  setField,
  setFields,
} from "@/utils/helpers";

type RouteTicketToGroupModalProps = {
  open: boolean;
  ticketId: number | null;
  currentGroup?: {
    id: number;
    groupName: string;
  } | null;
  onClose: () => void;
  onSuccess?: () => void;
};

type RouteTicketToGroupFormValues = {
  groupId: number | null;
  note: string;
};

type GroupOption = {
  value: number;
  label: string;
};

type RouteTicketToGroupState = {
  loading: boolean;
  error: string;
  options: GroupOption[];
  submitting: boolean;
};

const emptyForm: RouteTicketToGroupFormValues = {
  groupId: null,
  note: "",
};

const groupSelectStyles = {
  ...dropDownStyles,
  menuPortal: (base: Record<string, unknown>) => ({
    ...base,
    zIndex: 9999,
  }),
};

const createRouteTicketToGroupState = (): RouteTicketToGroupState => ({
  loading: false,
  error: "",
  options: [],
  submitting: false,
});

const routeTicketToGroupSchema: yup.ObjectSchema<RouteTicketToGroupFormValues> =
  yup.object({
    groupId: yup
      .number()
      .nullable()
      .required("Group is required"),
    note: yup.string().defined(),
  });

const toGroupOptions = (response: unknown): GroupOption[] => {
  const body = response as {
    data?: { data?: unknown } | unknown;
  } | null;
  const nestedData = body?.data;
  const rows = nestedData && typeof nestedData === "object" && "data" in nestedData
    ? nestedData.data
    : nestedData ?? response;

  if (!Array.isArray(rows)) return [];

  return rows.flatMap((row) => {
    const group = row as { id?: unknown; groupName?: unknown };
    const id = Number(group.id);
    if (!Number.isFinite(id)) return [];
    return [{
      value: id,
      label: typeof group.groupName === "string"
        ? group.groupName
        : `Group ${id}`,
    }];
  });
};

export default function RouteTicketToGroupModal({
  open,
  ticketId,
  currentGroup,
  onClose,
  onSuccess,
}: RouteTicketToGroupModalProps): React.JSX.Element {
  const [state, dispatch] = useReducer(
    Reducer,
    undefined,
    createRouteTicketToGroupState,
  ) as [RouteTicketToGroupState, React.Dispatch<ReducerAction>];
  const { loading, error, options, submitting: routingTicket } = state;
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<RouteTicketToGroupFormValues>({
    resolver: yupResolver(routeTicketToGroupSchema),
    defaultValues: emptyForm,
    mode: "all",
  });

  useEffect(() => {
    if (!open) return;

    let isActive = true;
    reset(emptyForm);

    const loadGroups = async () => {
      dispatch(setFields({ loading: true, error: "", options: [] }));
      try {
        const response = await SupervisorService.getAllGroups();
        if (!isActive) return;
        dispatch(setFields({
          options: toGroupOptions(response).filter(
            (group) => group.value !== currentGroup?.id,
          ),
        }));
      } catch {
        if (!isActive) return;
        dispatch(setFields({
          error: "Unable to load groups",
          options: [],
        }));
      } finally {
        if (isActive) dispatch(setField("loading", false));
      }
    };

    void loadGroups();
    return () => {
      isActive = false;
    };
  }, [currentGroup?.id, open, reset]);

  const closeDialog = () => {
    if (!routingTicket) onClose();
  };

  const submitRouting = async (values: RouteTicketToGroupFormValues) => {
    if (ticketId === null) return;

    dispatch(setField("submitting", true));
    try {
      const note = values.note.trim();
      const payload = {
        groupId: Number(values.groupId),
        ...(note ? { note } : {}),
      };
      if (currentGroup?.id) {
        await TicketService.reassignGroup(ticketId, payload);
      } else {
        await TicketService.routeToGroup(ticketId, payload);
      }
      onSuccess?.();
      onClose();
    } catch {
      // Request error presentation is handled by the global Axios interceptor.
    } finally {
      dispatch(setField("submitting", false));
    }
  };

  const noGroupsAvailable = !loading && !error && options.length === 0;
  const isMovingGroup = Boolean(currentGroup?.id);

  return (
    <Dialog open={open} onClose={closeDialog} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h4">
            {isMovingGroup ? "Move Ticket to Another Group" : "Escalate Ticket to Group"}
          </Typography>
          <IconButton
            aria-label="Close"
            onClick={closeDialog}
            disabled={routingTicket}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Box
        component="form"
        onSubmit={handleSubmit(submitRouting)}
        noValidate
      >
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {currentGroup ? (
              <Box
                sx={{
                  border: "1px solid #D6E4F5",
                  borderRadius: 1,
                  backgroundColor: "#F4F8FF",
                  p: 1.5,
                }}
              >
                <Typography color="text.secondary" fontSize={12}>
                  Current Group
                </Typography>
                <Typography color="primary.main" fontWeight={600} mt={0.5}>
                  {currentGroup.groupName}
                </Typography>
              </Box>
            ) : null}

            <FormControl fullWidth error={!!errors.groupId}>
              <Typography component="label" htmlFor="route-ticket-group" sx={{ mb: 0.75 }}>
                {isMovingGroup ? "New Group" : "Group"}
              </Typography>
              <Controller
                name="groupId"
                control={control}
                render={({ field }) => (
                  <Select
                    inputId="route-ticket-group"
                    aria-label={isMovingGroup ? "New Group" : "Group"}
                    options={options}
                    isLoading={loading}
                    isDisabled={loading}
                    noOptionsMessage={() => error || (
                      options.length ? "No matching groups" : "No groups available"
                    )}
                    value={options.find(
                      (group) => group.value === field.value,
                    ) ?? null}
                    onChange={(selected) => field.onChange(selected?.value ?? null)}
                    onBlur={field.onBlur}
                    isClearable
                    placeholder={isMovingGroup ? "Select New Group" : "Select Group"}
                    menuPortalTarget={document.body}
                    styles={groupSelectStyles}
                  />
                )}
              />
              {errors.groupId?.message ? (
                <FormHelperText>{errors.groupId.message}</FormHelperText>
              ) : null}
              {isMovingGroup && noGroupsAvailable ? (
                <FormHelperText>No other groups are available.</FormHelperText>
              ) : null}
            </FormControl>

            <AppTextArea
              name="note"
              label="Note (Optional)"
              control={control}
              rows={1}
              disabled={loading || routingTicket}
            />

          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <AppButton
            type="button"
            fullWidth
            variant="outlined"
            onClick={closeDialog}
            disabled={routingTicket}
          >
            Cancel
          </AppButton>
          <AppButton
            type="submit"
            fullWidth
            variant="contained"
            disabled={
              routingTicket
              || loading
              || Boolean(error)
              || noGroupsAvailable
            }
            loading={routingTicket}
            loadingText={isMovingGroup ? "Moving ticket..." : "Escalating..."}
          >
            {isMovingGroup ? "Move Ticket" : "Escalate"}
          </AppButton>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
